import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import { RootState } from "@Reducers/index";
import { setShowPortalChat } from "../../../reducers/portalChat";
import { usePortalChatCore } from "../core/usePortalChatCore";
import type { PortalChatPlatform } from "../core/platform";
import { PortalChatContext } from "./context";
import ChatListPane from "./ChatListPane";
import ChatThreadPane from "./ChatThreadPane";
import { ForwardDialog, ReceiptsDialog, ReactionsDialog, EditMessageDialog } from "./ChatDialogs";

/**
 * Web platform adapter for the shared chat core: per-tab persistence via sessionStorage, tab
 * visibility via the document, cookie-authenticated socket (no extra connect options), and the
 * PRD §4.1 reopen-on-last-conversation behaviour.
 */
const webPlatform: PortalChatPlatform = {
  storage: {
    getItem: (key) => {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        // sessionStorage may be unavailable; persistence is best-effort.
      }
    },
  },
  isAppVisible: () => document.visibilityState === "visible",
  subscribeAppVisibility: (onChange) => {
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  },
  restoreLastConversationOnOpen: true,
  forceProjectScopeOnOpen: true,
  autoSelectFallbackOnRemoval: true,
};

/**
 * Portal Chat — the slide-out panel shell (PRD §panel): floating above the page, anchored to the
 * end/bottom with a 14px gap, maximize to full-bleed below the header, close resets maximize.
 * Mounted once at Router level (like the Notification panel) so it survives route changes.
 * All chat STATE lives in the shared core (../core/usePortalChatCore) — this file is web chrome.
 */
function PortalChatPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const show = useSelector((state: RootState) => state.showPortalChat);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const userId = String(userDetails._id ?? userDetails.id ?? "");
  const userName = `${userDetails.firstName ?? ""} ${userDetails.lastName ?? ""}`.trim();

  const [maximized, setMaximized] = useState(false);
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState<HTMLElement | null>(null);

  const { pathname } = useLocation();
  const currentProjectId = useMemo(() => {
    if (!show) return null;
    const match = /\/project\/([0-9a-fA-F]{24})/.exec(pathname);
    return match?.[1] ?? null;
  }, [show, pathname]);

  const { contextValue, connected, dialogs, bumpMenuEpoch } = usePortalChatCore({
    show,
    userId,
    userName,
    currentProjectId,
    platform: webPlatform,
  });
  const { scope, setScope } = contextValue;

  const close = () => {
    dispatch(setShowPortalChat(false));
    setMaximized(false); // PRD: closing resets maximize
  };

  if (!show || !userId) return null;

  return (
    <PortalChatContext.Provider value={contextValue}>
      <div
        data-testid="portalChatPanel"
        className={`fixed z-1200 flex flex-col overflow-hidden rounded-lg border border-[#dce6f1] bg-white shadow-[0_1.25rem_2.5rem_0_rgba(106,138,177,0.35)] ${
          maximized
            ? "inset-x-0 bottom-0 top-[4.7rem] rounded-none"
            : "bottom-3.5 inset-e-3.5 top-22 w-261.5 max-w-[calc(100vw-1.75rem)]"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[#e8f1fc] bg-[#f0f5f9] px-4 py-2">
          <button
            type="button"
            data-testid="portalChatScope"
            className="flex items-center gap-1 text-base font-semibold"
            onClick={(e) => setScopeMenuAnchor(e.currentTarget)}
          >
            {scope === "all"
              ? t("All Chats")
              : scope === "direct"
                ? t("Direct Chats")
                : scope === "group"
                  ? t("Group Chats")
                  : t("Project Chats")}
            <IconComponent name="BottomArrow" width="0.7rem" height="0.7rem" />
          </button>
          <div className="flex items-center gap-3">
            {!connected && (
              <span className="rounded-xs bg-[#fff3e0] px-2 py-0.5 text-xs text-[#b26a00]">{t("Reconnecting…")}</span>
            )}
            <IconComponent
              name={maximized ? "exitFullScreen" : "enterFullScreen"}
              width="1.1rem"
              height="1.1rem"
              toolTipContent={maximized ? t("Restore panel") : t("Maximise panel")}
              onClick={() => {
                setMaximized((prev) => !prev);
                bumpMenuEpoch(); // PRD §4.6: open menus close on maximise/restore
                setScopeMenuAnchor(null);
              }}
            />
            <IconComponent name="CrossIcon" width="1rem" height="1rem" toolTipContent={t("Close")} onClick={close} />
          </div>
        </div>
        {scopeMenuAnchor && (
          <MenuComponent anchorEl={scopeMenuAnchor} open onClose={() => setScopeMenuAnchor(null)} width="12rem">
            {(["all", "direct", "group", "project"] as const).map((option) => (
              <MenuItemComponent
                key={option}
                onClick={() => {
                  setScope(option);
                  setScopeMenuAnchor(null);
                }}
              >
                {option === "all"
                  ? t("All Chats")
                  : option === "direct"
                    ? t("Direct Chats")
                    : option === "group"
                      ? t("Group Chats")
                      : t("Project Chats")}
              </MenuItemComponent>
            ))}
          </MenuComponent>
        )}
        <div className="flex min-h-0 flex-1">
          <ChatListPane />
          <ChatThreadPane />
        </div>
      </div>
      {dialogs.forwardMessage && <ForwardDialog message={dialogs.forwardMessage} onClose={dialogs.closeForward} />}
      {dialogs.receiptsMessage && <ReceiptsDialog message={dialogs.receiptsMessage} onClose={dialogs.closeReceipts} />}
      {dialogs.reactionsMessage && (
        <ReactionsDialog message={dialogs.reactionsMessage} onClose={dialogs.closeReactions} />
      )}
      {dialogs.editDialogMessage && (
        <EditMessageDialog message={dialogs.editDialogMessage} onClose={dialogs.closeEdit} />
      )}
    </PortalChatContext.Provider>
  );
}

export default PortalChatPanel;
